/**
 * ADMALL INDIA — Meta Pixel Tracking Utility
  *
 * Features:
 * - SPA Route-Aware PageView deduplication
 * - Strict PII sanitization (Zero personal info: no names, phones, emails, or personal messages sent to Meta)
 * - Administrative route exclusion (/admin)
 * - Safe error handling (graceful fallback if blocked by ad-blocker or offline)
 * - Cookie consent & opt-out compliance hook
 */

export const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "4381907418741692";

// Strictly prohibited parameter keys that must never be transmitted to Meta
const SENSITIVE_KEYS = new Set([
  "email",
  "phone",
  "mobile",
  "name",
  "full_name",
  "first_name",
  "last_name",
  "password",
  "address",
  "message",
  "objective",
  "city",
  "company",
  "website",
  "best_time",
]);

/**
 * Checks if the browser has tracking consent.
 * Strict Opt-In: returns true ONLY when visitor explicitly granted consent ('accepted').
 * If no decision exists (null) or user declined ('declined'), returns false.
 */
export function hasTrackingConsent() {
  if (typeof window === "undefined") return false;
  try {
    const consent = localStorage.getItem("admall_cookie_consent");
    return consent === "accepted";
  } catch {
    return false;
  }
}

/**
 * Returns current consent status: 'accepted' | 'declined' | null (no decision).
 */
export function getConsentDecision() {
  if (typeof window === "undefined") return null;
  try {
    const consent = localStorage.getItem("admall_cookie_consent");
    if (consent === "accepted" || consent === "declined") {
      return consent;
    }
  } catch {}
  return null;
}

/**
 * Checks whether the current path is an internal admin route.
 */
export function isAdminRoute(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  return pathname.startsWith("/admin");
}

/**
 * Strips all personal identifying information (PII) before transmission.
 */
export function sanitizeEventParams(params = {}) {
  if (!params || typeof params !== "object") return {};
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_KEYS.has(lowerKey)) {
      continue; // Strictly omit PII
    }
    // Inspect string values to prevent accidental email or phone leakage
    if (typeof value === "string") {
      const trimmed = value.trim();
      // Drop email-like values
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) continue;
      // Drop phone-like number sequences (7+ digits)
      if (/^[+0-9\s-]{7,16}$/.test(trimmed) && !lowerKey.includes("id")) continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
}

/**
 * Safely accesses window.fbq.
 */
function getFbq() {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    return window.fbq;
  }
  return null;
}

/**
 * Initializes Meta Pixel dynamically if consent is granted and not already loaded.
 * Ensures the script and init are executed strictly once.
 */
export function initMetaPixel() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (!hasTrackingConsent()) {
    return false;
  }
  if (window._metaPixelInitialized) {
    return true;
  }

  // Load fbevents.js only after consent is verified
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  if (typeof window.fbq === "function") {
    window.fbq("init", META_PIXEL_ID);
    window._metaPixelInitialized = true;
    return true;
  }
  return false;
}

/**
 * Grants tracking consent and activates tracking.
 */
export function grantTrackingConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("admall_cookie_consent", "accepted");
    window.dispatchEvent(new CustomEvent("admall_consent_changed", { detail: { consent: "accepted" } }));
  } catch {}
  initMetaPixel();
  if (typeof window.location !== "undefined") {
    trackPageView(window.location.pathname);
  }
}

/**
 * Revokes tracking consent and prevents further tracking.
 */
export function revokeTrackingConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("admall_cookie_consent", "declined");
    window.dispatchEvent(new CustomEvent("admall_consent_changed", { detail: { consent: "declined" } }));
  } catch {}
  lastDispatchedPathname = null;
  const fbq = getFbq();
  if (fbq) {
    try {
      fbq("consent", "revoke");
    } catch {}
  }
}

// Track last dispatched path to prevent duplicate PageView events in React 19 / Fast Refresh
let lastDispatchedPathname = null;

/**
 * Track SPA PageView on route changes.
 * Skips duplicate calls for the same route and skips /admin routes.
 */
export function trackPageView(pathname = typeof window !== "undefined" ? window.location.pathname : "") {
  if (!hasTrackingConsent()) {
    return false;
  }
  if (isAdminRoute(pathname)) {
    return false;
  }
  if (lastDispatchedPathname === pathname) {
    return false; // Prevent duplicate PageView
  }

  // Ensure Pixel is initialized if consent is active
  initMetaPixel();

  const fbq = getFbq();
  if (fbq) {
    lastDispatchedPathname = pathname;
    fbq("track", "PageView");
    if (process.env.NODE_ENV !== "production") {
      console.debug(`[MetaPixel] PageView tracked -> ${pathname}`);
    }
    return true;
  }
  return false;
}

/**
 * Track successful Lead event.
 * MUST be invoked only after a successful /api/leads backend response.
 * Never receives or sends PII to Meta.
 */
export function trackLead({ source = "contact", requirement = "", budget = "" } = {}) {
  if (!hasTrackingConsent()) return false;
  const fbq = getFbq();
  if (!fbq) return false;

  const rawParams = {
    content_name: "Lead Consultation Request",
    content_category: requirement || "General Inquiry",
    lead_source: source,
    budget_range: budget || "Unspecified",
  };

  const safeParams = sanitizeEventParams(rawParams);
  fbq("track", "Lead", safeParams);

  if (process.env.NODE_ENV !== "production") {
    console.debug("[MetaPixel] Lead event tracked successfully:", safeParams);
  }
  return true;
}

/**
 * Track Contact interaction events (WhatsApp, Phone call, Email).
 */
export function trackContact(channel, { location = "", label = "" } = {}) {
  if (!hasTrackingConsent()) return false;
  const fbq = getFbq();
  if (!fbq) return false;

  const rawParams = {
    channel: channel, // 'whatsapp' | 'phone' | 'email'
    touchpoint_location: location,
    cta_label: label,
  };

  const safeParams = sanitizeEventParams(rawParams);
  fbq("track", "Contact", safeParams);

  if (process.env.NODE_ENV !== "production") {
    console.debug(`[MetaPixel] Contact (${channel}) event tracked:`, safeParams);
  }
  return true;
}

/**
 * Track ViewContent on key service, industry, or portfolio items.
 */
export function trackViewContent({ title = "", category = "", slug = "" } = {}) {
  if (!hasTrackingConsent()) return false;
  const fbq = getFbq();
  if (!fbq) return false;

  const rawParams = {
    content_name: title,
    content_category: category,
    content_ids: slug ? [slug] : undefined,
    content_type: "service",
  };

  const safeParams = sanitizeEventParams(rawParams);
  fbq("track", "ViewContent", safeParams);

  if (process.env.NODE_ENV !== "production") {
    console.debug("[MetaPixel] ViewContent event tracked:", safeParams);
  }
  return true;
}

/**
 * Generic custom event dispatcher with automatic PII sanitization.
 */
export function trackCustomEvent(eventName, params = {}) {
  if (!hasTrackingConsent()) return false;
  const fbq = getFbq();
  if (!fbq) return false;

  const safeParams = sanitizeEventParams(params);
  fbq("trackCustom", eventName, safeParams);
  return true;
}
